import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import { Button, CheckBox } from "devextreme-react";
import {
    DataGrid,
    Column,
    Editing,
    FilterRow,
    Lookup,
    Paging,
    RangeRule,
    RequiredRule,
    Scrolling,
    Selection,
    Summary,
    TotalItem,
    LoadPanel as GridLoadPanel,
} from "devextreme-react/data-grid";
import { LoadPanel } from "devextreme-react/load-panel";
import { createStore } from "devextreme-aspnet-data-nojquery";
import { alert } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
import { CONCURRENCY_HANDLED, getApiErrorMessage } from "../../../../../common/concurrency";
import { discardPendingEdits } from "../../../../../common/dataStore";
import { LocationStatus, LocationTypes } from "../../../../../enum/Enum";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import { OperationDialogFooter } from "./OperationDialogParts";

/*
 * Charge & Hold Shipment - Actual Ship Items (Delphi uOMD0037), opened by the "Change" button of the
 * order entry detail tab "C&H Ship Inv". The grid is a working copy held here, exactly as Delphi held
 * it in a client dataset: the buttons and cell edits only change this copy, and Save sends the whole
 * set, which the server writes in one transaction (delete the line's ship rows, create one per grid
 * row, re-derive the line's stage quantities). That makes this an operation, not grid CRUD — there is
 * no Add/Edit endpoint per row.
 */

const API = window._env_.REACT_APP_APIS;
const quantityFormat = { type: "fixedPoint", precision: 2 };
// Delphi's footer: red outside 0..ordered, yellow below ordered, default when equal.
const TOTAL_OVER_COLOR = "#ff6b6b";
const TOTAL_UNDER_COLOR = "#ffe066";

// The header is a dynamic form, so its layout can be rearranged per user like the other order entry forms.
const HEADER_LAYOUT_ID = "8FDFE81D-1881-442D-BA7B-2A6124E8FAB1";
const HEADER_LAYOUT_NAME = "OMChargeAndHoldShipmentForm";
const HEADER_TABLE_NAME = "ChargeAndHoldShipment";

// uOMD0037 pnlTop: the two group boxes and their captions. Every field is display-only (qryTransInfo),
// and the item fields are the SALES item's, not a ship row's.
const headerField = (name, label, type = "text", extra = {}) => ({
    name,
    tableName: HEADER_TABLE_NAME,
    label,
    type,
    required: false,
    visible: true,
    readOnly: true,
    ...extra,
});
const headerFormSchema = [
    {
        groupId: 1,
        label: "Transaction Information",
        fields: [
            headerField("TransactionNumber", "Transaction #"),
            headerField("TransactionDate", "Trans Date", "date"),
            headerField("CustomerId", "Customer ID"),
            headerField("CustomerName", "Cust Name"),
        ],
    },
    {
        groupId: 2,
        label: "Sale/Ship Item Information",
        fields: [
            headerField("SalesItemWarehouse", "Item WH"),
            headerField("SalesItemCode", "Item Code"),
            headerField("SalesItemDescription1", "Item Desc 1"),
            headerField("SalesItemDescription2", "Item Desc 2"),
            headerField("QuantityOrdered", "Ordered Qnty", "number", { format: quantityFormat }),
        ],
    },
];
const emptyHeader = {};
// The header is never edited, so FormField's change callback has nothing to write back.
const ignoreHeaderChange = () => {};

// IMLocation code lists; the captions are verbatim from qryIMLocation (uOMD0037.dfm), shown in the location pickers.
const locationTypeOptions = [
    { id: LocationTypes.DockDoor, text: "Dock/Door" },
    { id: LocationTypes.ForwardPick, text: "Forward Pick" },
    { id: LocationTypes.StagingLocation, text: "Staging" },
    { id: LocationTypes.Replenishment, text: "Replenishment" },
    { id: LocationTypes.StorageLocation, text: "Storage" },
    { id: LocationTypes.Yard, text: "Yard" },
    { id: LocationTypes.ProductionLocation, text: "Production" },
];
// cdsGridDataLocationCodeSetText accepts a typed location only in these statuses (qryIMLocation's 'A','S').
const shippingLocationStatuses = [LocationStatus.AllowAll, LocationStatus.ShipmentsOnly];
const locationStatusOptions = [
    { id: LocationStatus.AllowAll, text: "Allow All" },
    { id: LocationStatus.ReceiptsOnly, text: "Allow Receipts Only" },
    { id: LocationStatus.ShipmentsOnly, text: "Allow Shipments Only" },
    { id: LocationStatus.NotAllowed, text: "Disable All" },
];

const format = (template, ...args) => args.reduce((text, arg, i) => text.replace(`{${i}}`, arg ?? ""), template);

const totalOf = (rows) => rows.reduce((sum, row) => sum + (Number(row.Originalquantity) || 0), 0);

/** uOMD0037 GetRemainingShipQuantity: what is still to ship, never below zero. */
const remainingOf = (rows, ordered) => Math.max(0, (Number(ordered) || 0) - totalOf(rows));

/** uOMD0037 CalculateLineQuantity: a new row takes what is available, up to what is still to ship. */
const lineQuantity = (remaining, available) =>
    remaining <= 0 || available <= 0 ? 0 : Math.min(available, remaining);

/** First page of a DevExtreme data result, whichever shape the store hands back. */
const itemsOf = (result) => (Array.isArray(result) ? result : result?.data || []);

const pickerColumns = {
    // The sort orders are each Delphi query's ORDER BY.
    // qryIMLocationQuantitySummary — "Add Lot & Location".
    inventory: [
        { dataField: "lotcode", caption: "Lot", sortOrder: "asc", sortIndex: 0 },
        { dataField: "description1", caption: "Lot Description 1" },
        { dataField: "warehouse", caption: "WH", sortOrder: "asc", sortIndex: 1 },
        { dataField: "LocationCode", caption: "Location", sortOrder: "asc", sortIndex: 2 },
        { dataField: "Priority", caption: "Priority" },
        { dataField: "LocationType", caption: "Location Type", lookup: locationTypeOptions },
        { dataField: "Available", caption: "Available", dataType: "number", format: quantityFormat },
        { dataField: "OnHand", caption: "On Hand", dataType: "number", format: quantityFormat },
        { dataField: "Netavailable", caption: "Net Available", dataType: "number", format: quantityFormat },
        { dataField: "AvailableDate", caption: "Available Dt", dataType: "date" },
        { dataField: "ExpiryDate", caption: "Expiration Dt", dataType: "date" },
        { dataField: "classcode", caption: "Class" },
        { dataField: "Subclasscode", caption: "Sub Class" },
    ],
    // qryIMLot — "Add Lot Only" and the Lot cell.
    lot: [
        { dataField: "lotcode", caption: "Lot", sortOrder: "asc", sortIndex: 0 },
        { dataField: "description1", caption: "Description - 1" },
        { dataField: "classcode", caption: "Class" },
        { dataField: "Subclasscode", caption: "Sub Class" },
        { dataField: "AvailableDate", caption: "Available Dt", dataType: "date" },
        { dataField: "ExpiryDate", caption: "Expiration Dt", dataType: "date" },
        { dataField: "OnHand", caption: "On Hand", dataType: "number", format: quantityFormat },
        { dataField: "Available", caption: "Available", dataType: "number", format: quantityFormat },
    ],
    // qryIMLocation — the Location cell.
    location: [
        { dataField: "IdentityId", caption: "WH" },
        { dataField: "LocationCode", caption: "Location", sortOrder: "asc", sortIndex: 0 },
        { dataField: "Description", caption: "Description" },
        { dataField: "LocationType", caption: "Location Type", lookup: locationTypeOptions },
        { dataField: "Status", caption: "Status", lookup: locationStatusOptions },
        { dataField: "Priority", caption: "Priority" },
    ],
};

/** The search dialog behind the add buttons and the Lot / Location cell buttons (Delphi PerformUserSearch). */
function ShipItemPicker({ picker, onPick, onClose, onOpenStatusOnlyChange }) {
    const gridRef = useRef(null);
    const pickSelected = useCallback(() => {
        const row = gridRef.current?.instance()?.getSelectedRowsData()?.[0];
        if (row) onPick(row);
    }, [onPick]);

    if (!picker) return null;

    return (
        <Popup
            visible={true}
            title={picker.title}
            onHiding={onClose}
            showCloseButton={true}
            hideOnOutsideClick={false}
            container="#root"
            width="80vw"
            height="70vh"
        >
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                {/* PerformUserSearch's "Open status only" box: shown, ticked, for a query with OpenStatusOnly (qryIMLot). */}
                {picker.openStatusOnly !== undefined && (
                    <div style={{ flexShrink: 0, paddingBottom: 8 }}>
                        <CheckBox
                            text={MESSAGES.CH_SHIP_OPEN_STATUS_ONLY}
                            value={picker.openStatusOnly}
                            onValueChanged={(e) => onOpenStatusOnlyChange(e.value)}
                        />
                    </div>
                )}
                <div style={{ flex: "1 1 auto", minHeight: 0 }}>
                    <DataGrid
                        ref={gridRef}
                        dataSource={picker.dataSource}
                        remoteOperations={true}
                        height="100%"
                        showBorders={true}
                        columnAutoWidth={true}
                        allowColumnResizing={true}
                        hoverStateEnabled={true}
                        onRowDblClick={(e) => onPick(e.data)}
                    >
                        <Selection mode="single" />
                        <FilterRow visible={true} />
                        <Paging defaultPageSize={20} />
                        <GridLoadPanel enabled={true} />
                        {picker.columns.map(({ lookup, ...column }) => (
                            <Column
                                key={column.dataField}
                                {...column}
                                filterValue={picker.filterValues?.[column.dataField]}
                            >
                                {lookup && <Lookup dataSource={lookup} displayExpr="text" valueExpr="id" />}
                            </Column>
                        ))}
                    </DataGrid>
                </div>
                <OperationDialogFooter okText="Select" onOk={pickSelected} onCancel={onClose} />
            </div>
        </Popup>
    );
}

function ChargeAndHoldShipmentPopup(props) {
    const { showPopup = {}, setShowPopup, onSaved, oidc } = props;
    const visible = showPopup.visible === true;
    const detailRowId = showPopup.detailRowId;
    const currentUser = oidc?.user;
    const accessToken = currentUser?.access_token;
    const DYNAMICFORMSETTING = currentUser?.profile?.DYNAMICFORMSETTING;

    const gridRef = useRef(null);
    const formRefs = useRef({});
    const [shipment, setShipment] = useState(null);
    const [rows, setRows] = useState([]);
    const [focusedRowKey, setFocusedRowKey] = useState(null);
    const [picker, setPicker] = useState(null);
    const [busy, setBusy] = useState(false);

    const headers = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);
    const quantityOrdered = Number(shipment?.QuantityOrdered) || 0;

    const closePopup = useCallback(() => {
        setShipment(null);
        setRows([]);
        setFocusedRowKey(null);
        setPicker(null);
        setShowPopup((prev) => ({ ...prev, visible: false }));
    }, [setShowPopup]);

    // uOMD0037 SetTransDetail. The server re-checks the entry gate (stage, lot, Charge & Hold
    // shipment) and refuses with its message, which closes the dialog before it is used.
    useEffect(() => {
        if (!visible || !detailRowId || !accessToken) return undefined;
        let cancelled = false;
        setBusy(true);
        axios
            .get(`${API}/${routes.OM.GetChargeAndHoldShipment}`, { params: { detailRowId }, headers })
            .then((res) => {
                if (cancelled) return;
                const data = res.data || {};
                setShipment(data);
                setRows(data.Locations || []);
                setFocusedRowKey(data.Locations?.[0]?.Rowid ?? null);
            })
            .catch((err) => {
                if (cancelled || err?.[CONCURRENCY_HANDLED]) return;
                alert(getApiErrorMessage(err), MESSAGES.ERROR).then(closePopup);
            })
            .finally(() => {
                if (!cancelled) setBusy(false);
            });
        return () => {
            cancelled = true;
        };
    }, [visible, detailRowId, accessToken, headers, closePopup]);

    const makeStore = useCallback(
        (key, route, loadParams) =>
            createStore({
                key,
                loadUrl: `${API}/${route}`,
                loadParams,
                onBeforeSend: (method, ajaxOptions) => {
                    ajaxOptions.headers = { Authorization: `Bearer ${accessToken}` };
                },
            }),
        [accessToken]
    );

    // openStatusOnly defaults to true: the typed lot (cdsGridDataLotCodeSetText) is always held to Active lots.
    const lotStore = useCallback(
        (itemId, openStatusOnly = true) =>
            makeStore("rowid", routes.OM.GetChargeAndHoldShipmentLots, { itemId, openStatusOnly }),
        [makeStore]
    );
    const locationStore = useMemo(
        () => makeStore("RowId", routes.OM.GetChargeAndHoldShipmentWarehouseLocations),
        [makeStore]
    );
    // The typed Location cell looks the code up whatever its status, so it can say why one is refused.
    const anyStatusLocationStore = useMemo(
        () => makeStore("RowId", routes.OM.GetChargeAndHoldShipmentWarehouseLocations, { anyLocationStatus: true }),
        [makeStore]
    );

    // A pending cell edit would be applied on top of a row the picker or a button has just replaced.
    const dropPendingEdit = useCallback(() => discardPendingEdits(gridRef.current?.instance()), []);

    const updateRow = useCallback((rowKey, changes) => {
        setRows((prev) => prev.map((row) => (row.Rowid === rowKey ? { ...row, ...changes } : row)));
    }, []);

    const appendRow = useCallback((row) => {
        setRows((prev) => [...prev, row]);
        setFocusedRowKey(row.Rowid);
    }, []);

    // ---- toolbar ------------------------------------------------------------------------------

    /** Auto-Assign Ship Inventory: replaces the grid with the database's own assignment, unsaved edits included (as Delphi). */
    const handleAutoAssign = useCallback(async () => {
        dropPendingEdit();
        setBusy(true);
        try {
            const res = await axios.get(`${API}/${routes.OM.GetChargeAndHoldShipmentAutoAssignedLocations}`, {
                params: { detailRowId },
                headers,
            });
            const assigned = Array.isArray(res.data) ? res.data : [];
            setRows(assigned);
            setFocusedRowKey(assigned[0]?.Rowid ?? null);
        } catch (err) {
            if (!err?.[CONCURRENCY_HANDLED]) alert(getApiErrorMessage(err), MESSAGES.ERROR);
        } finally {
            setBusy(false);
        }
    }, [dropPendingEdit, detailRowId, headers]);

    const openInventoryPicker = useCallback(() => {
        dropPendingEdit();
        setPicker({
            kind: "inventory",
            title: "Add Lot & Location",
            columns: pickerColumns.inventory,
            // Always the SALES item (qryTransInfoR_ItemSales), whatever row is focused.
            dataSource: makeStore("rowid", routes.OM.GetChargeAndHoldShipmentInventory, {
                itemId: shipment?.SalesItemRowId,
            }),
        });
    }, [dropPendingEdit, makeStore, shipment]);

    /** kind "addLot" = Add Lot Only (rowKey = the focused row), "changeLot" = the Lot cell's button. */
    const openLotPicker = useCallback(
        (kind, rowKey) => {
            dropPendingEdit();
            const row = rows.find((x) => x.Rowid === rowKey);
            // Delphi's lot search used the focused row's item, which left it empty with no row focused;
            // the sales item stands in for that case.
            const itemId = row?.RItem || shipment?.SalesItemRowId;
            setPicker({
                kind,
                rowKey,
                itemId,
                title: "Lot",
                columns: pickerColumns.lot,
                openStatusOnly: true,
                dataSource: lotStore(itemId, true),
            });
        },
        [dropPendingEdit, rows, shipment, lotStore]
    );

    /** The lot picker's "Open status only" box: unticked, lots in any status are listed. */
    const handleOpenStatusOnlyChange = useCallback(
        (openStatusOnly) => {
            setPicker((prev) =>
                prev ? { ...prev, openStatusOnly, dataSource: lotStore(prev.itemId, openStatusOnly) } : prev
            );
        },
        [lotStore]
    );

    const openLocationPicker = useCallback(
        (rowKey) => {
            dropPendingEdit();
            const row = rows.find((x) => x.Rowid === rowKey);
            setPicker({
                kind: "location",
                rowKey,
                title: "Location",
                columns: pickerColumns.location,
                dataSource: locationStore,
                // Delphi opened the search pre-filled with the row's warehouse; the user may clear it.
                filterValues: { IdentityId: row?.Warehouse || undefined },
            });
        },
        [dropPendingEdit, rows, locationStore]
    );

    const handlePick = useCallback(
        (picked) => {
            const current = picker;
            setPicker(null);
            if (!current || !picked) return;

            if (current.kind === "inventory") {
                // uOMD0037 AddLotAndLocation.
                const row = {
                    Rowid: uuidv4(),
                    RItem: picked.RItem,
                    ItemCode: picked.itemcode,
                    RLot: picked.RLot,
                    LotCode: picked.lotcode,
                    Originalquantity: lineQuantity(remainingOf(rows, quantityOrdered), Number(picked.Available) || 0),
                };
                if (picked.RLocation) {
                    Object.assign(row, {
                        RWarehouse: picked.RWarehouse,
                        Warehouse: picked.warehouse,
                        RLocation: picked.RLocation,
                        Location: picked.LocationCode,
                    });
                }
                appendRow(row);
                return;
            }

            if (current.kind === "addLot") {
                // uOMD0037 AddLotOnly: a row with no location.
                appendRow({
                    Rowid: uuidv4(),
                    RItem: picked.RItem,
                    ItemCode: picked.itemcode,
                    RLot: picked.RLot,
                    LotCode: picked.lotcode,
                    Originalquantity: lineQuantity(remainingOf(rows, quantityOrdered), Number(picked.Available) || 0),
                });
                return;
            }

            if (current.kind === "changeLot") {
                // uOMD0037 LookupAndChangeLot: the quantity is only ever lowered to what the lot has.
                const row = rows.find((x) => x.Rowid === current.rowKey);
                const available = Number(picked.Available) || 0;
                updateRow(current.rowKey, {
                    RLot: picked.RLot,
                    LotCode: picked.lotcode,
                    ...(row && available < (Number(row.Originalquantity) || 0) ? { Originalquantity: available } : {}),
                });
                return;
            }

            if (current.kind === "location") {
                // uOMD0037 LookupLocation.
                updateRow(current.rowKey, {
                    RWarehouse: picked.RWarehouse,
                    Warehouse: picked.IdentityId,
                    RLocation: picked.RowId,
                    Location: picked.LocationCode,
                });
            }
        },
        [picker, rows, quantityOrdered, appendRow, updateRow]
    );

    /** Remove Line: the focused row, from the working copy only. */
    const handleRemoveLine = useCallback(() => {
        const index = rows.findIndex((x) => x.Rowid === focusedRowKey);
        if (index < 0) return;
        dropPendingEdit();
        const next = rows.filter((x) => x.Rowid !== focusedRowKey);
        setRows(next);
        // Delphi refocused the row that took the removed one's place.
        setFocusedRowKey(next[Math.min(index, next.length - 1)]?.Rowid ?? null);
    }, [rows, focusedRowKey, dropPendingEdit]);

    const handleRemoveAll = useCallback(() => {
        dropPendingEdit();
        setRows([]);
        setFocusedRowKey(null);
    }, [dropPendingEdit]);

    // ---- typed cells (Delphi OnSetText) ---------------------------------------------------------

    /** cdsGridDataLotCodeSetText: a typed lot must be one of the item's physical lots with stock. */
    const setLotCellValue = useCallback(
        async (newData, value, currentRowData) => {
            const entry = (value ?? "").trim();
            const keepCurrent = () => {
                newData.LotCode = currentRowData.LotCode;
            };
            if (!entry) {
                alert(MESSAGES.CH_SHIP_LOT_BLANK, MESSAGES.ERROR);
                keepCurrent();
                return;
            }
            const itemId = currentRowData.RItem || shipment?.SalesItemRowId;
            const lot = itemsOf(await lotStore(itemId).load({ filter: ["lotcode", "=", entry], take: 1 }))[0];
            if (!lot) {
                alert(format(MESSAGES.CH_SHIP_LOT_NOT_FOUND, entry), MESSAGES.ERROR);
                keepCurrent();
                return;
            }
            newData.RLot = lot.RLot;
            newData.LotCode = lot.lotcode;
            const available = Number(lot.Available) || 0;
            if ((Number(currentRowData.Originalquantity) || 0) > available) newData.Originalquantity = available;
        },
        [shipment, lotStore]
    );

    /** cdsGridDataLocationCodeSetText: blank clears the location; anything else must exist in the row's warehouse. */
    const setLocationCellValue = useCallback(
        async (newData, value, currentRowData) => {
            const entry = (value ?? "").trim();
            if (!entry) {
                Object.assign(newData, { RWarehouse: null, Warehouse: null, RLocation: null, Location: null });
                return;
            }
            const byCode = ["LocationCode", "=", entry];
            // Delphi trims the row's LocationWH in both messages; the code column is space-padded.
            const warehouseCode = (currentRowData.Warehouse ?? "").trim();
            // A row with no warehouse yet (Add Lot Only) has nothing to narrow by — Delphi's SQL failed
            // there. A location code that is unique across warehouses is accepted instead.
            const filter = currentRowData.RWarehouse ? [["RWarehouse", "=", currentRowData.RWarehouse], "and", byCode] : byCode;
            const matches = itemsOf(await anyStatusLocationStore.load({ filter, take: 2 }));
            if (matches.length !== 1) {
                alert(format(MESSAGES.CH_SHIP_LOCATION_NOT_FOUND, warehouseCode, entry), MESSAGES.ERROR);
                newData.Location = currentRowData.Location;
                return;
            }
            const location = matches[0];
            // Found, but it does not allow shipments: Delphi's second, separate refusal.
            if (!shippingLocationStatuses.includes(location.Status)) {
                alert(format(MESSAGES.CH_SHIP_LOCATION_NO_SHIPMENTS, warehouseCode, entry), MESSAGES.ERROR);
                newData.Location = currentRowData.Location;
                return;
            }
            Object.assign(newData, {
                RWarehouse: location.RWarehouse,
                Warehouse: location.IdentityId,
                RLocation: location.RowId,
                Location: location.LocationCode,
            });
        },
        [anyStatusLocationStore]
    );

    // The Lot and Location cells keep Delphi's button columns: the button opens the picker for that row.
    const onEditorPreparing = useCallback(
        (e) => {
            if (e.parentType !== "dataRow") return;
            const rowKey = e.row?.key;
            if (e.dataField === "LotCode" || e.dataField === "Location") {
                const open = e.dataField === "LotCode"
                    ? () => openLotPicker("changeLot", rowKey)
                    : () => openLocationPicker(rowKey);
                e.editorOptions.buttons = [
                    {
                        name: "lookup",
                        location: "after",
                        options: { icon: "more", stylingMode: "text", onClick: open },
                    },
                ];
            }
        },
        [openLotPicker, openLocationPicker]
    );

    const onCellPrepared = useCallback(
        (e) => {
            if (e.rowType !== "totalFooter" || e.column.dataField !== "Originalquantity") return;
            const total = Number(e.summaryItems?.[0]?.value) || 0;
            e.cellElement.style.fontWeight = "bold";
            if (total < 0 || total > quantityOrdered) e.cellElement.style.backgroundColor = TOTAL_OVER_COLOR;
            else if (total < quantityOrdered) e.cellElement.style.backgroundColor = TOTAL_UNDER_COLOR;
        },
        [quantityOrdered]
    );

    // ---- save ---------------------------------------------------------------------------------

    /** uOMD0037 SaveChanges. The refusals (no rows, total out of range, a row without item/lot/quantity) come from the server. */
    const handleSave = useCallback(async () => {
        // Nothing to save until the line has loaded, and not while a load or a save is in flight.
        if (!shipment || busy) return;
        const grid = gridRef.current?.instance();
        if (grid?.hasEditData()) await grid.saveEditData();
        // A cell that failed its own validation is still pending; leave it for the user to fix.
        if (grid?.hasEditData()) return;

        setBusy(true);
        try {
            const payload = {
                DetailRowId: shipment.DetailRowId,
                ConcurrencyToken: shipment.ConcurrencyToken,
                Locations: rows.map((row) => ({
                    Rowid: row.Rowid,
                    RItem: row.RItem,
                    RLot: row.RLot,
                    RWarehouse: row.RWarehouse,
                    RLocation: row.RLocation,
                    Originalquantity: row.Originalquantity,
                })),
            };
            const res = await axios.post(`${API}/${routes.OM.UpdateChargeAndHoldShipmentLocations}`, payload, { headers });
            notify({ message: res.data?.message, width: 300 }, "success", 3000);
            if (onSaved) onSaved();
            closePopup();
        } catch (err) {
            if (!err?.[CONCURRENCY_HANDLED]) alert(getApiErrorMessage(err), MESSAGES.ERROR);
        } finally {
            setBusy(false);
        }
    }, [shipment, busy, rows, headers, onSaved, closePopup]);

    if (!visible) return null;

    return (
        <Popup
            visible={true}
            onHiding={closePopup}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title="Charge & Hold Shipment - Actual Ship Items"
            container="#root"
            width="85vw"
            height="90vh"
        >
            <LoadPanel visible={busy} shading={false} position={{ of: "#root" }} />
            <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ flexShrink: 0 }}>
                    {/* Mounted with the popup, so it is fresh on every open; the layout is static and the
                        values follow `shipment` as it loads. */}
                    <FormField
                        currrentUser={currentUser}
                        layoutId={HEADER_LAYOUT_ID}
                        layoutName={HEADER_LAYOUT_NAME}
                        defaultFormSchema={headerFormSchema}
                        formFields={shipment ?? emptyHeader}
                        setformFields={ignoreHeaderChange}
                        DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                        formRefs={formRefs}
                        noSpacingBeforeFirstRow={true}
                    />
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button text="Auto-Assign Ship Inventory" disabled={!shipment || busy} onClick={handleAutoAssign} />
                    <Button text="Add Lot & Location" disabled={!shipment || busy} onClick={openInventoryPicker} />
                    <Button text="Add Lot Only" disabled={!shipment || busy} onClick={() => openLotPicker("addLot", focusedRowKey)} />
                    <div style={{ flex: "1 1 auto" }} />
                    <Button text="Remove Line" disabled={!focusedRowKey || busy} onClick={handleRemoveLine} />
                    <Button text="Remove All" disabled={rows.length === 0 || busy} onClick={handleRemoveAll} />
                </div>

                <div style={{ flex: "1 1 auto", minHeight: 0 }}>
                    <DataGrid
                        ref={gridRef}
                        dataSource={rows}
                        keyExpr="Rowid"
                        height="100%"
                        showBorders={true}
                        columnAutoWidth={true}
                        allowColumnResizing={true}
                        focusedRowEnabled={true}
                        focusedRowKey={focusedRowKey}
                        onFocusedRowChanged={(e) => setFocusedRowKey(e.row?.key ?? null)}
                        onEditorPreparing={onEditorPreparing}
                        onCellPrepared={onCellPrepared}
                    >
                        <Scrolling mode="standard" />
                        <Editing mode="cell" allowUpdating={!busy} />
                        <Column dataField="Rowid" visible={false} allowEditing={false} />
                        <Column dataField="ItemWarehouse" caption="Ship Item WH" visible={false} allowEditing={false} />
                        <Column dataField="ItemCode" caption="Ship Item" visible={false} allowEditing={false} />
                        <Column dataField="LotCode" caption="Lot" setCellValue={setLotCellValue} />
                        <Column dataField="Warehouse" caption="Warehouse" allowEditing={false} />
                        <Column dataField="Location" caption="Location" setCellValue={setLocationCellValue} />
                        <Column
                            dataField="Originalquantity"
                            caption="Ship Quantity"
                            dataType="number"
                            format={quantityFormat}
                            editorOptions={{ format: quantityFormat }}
                        >
                            <RequiredRule message={MESSAGES.CH_SHIP_QUANTITY_INVALID} />
                            <RangeRule min={0} message={MESSAGES.CH_SHIP_QUANTITY_INVALID} />
                        </Column>
                        <Summary>
                            <TotalItem
                                column="LotCode"
                                summaryType="count"
                                customizeText={(e) => format(MESSAGES.CH_SHIP_COUNT, e.value)}
                            />
                            {/* displayFormat "{0}" keeps DevExtreme's own "Sum: " prefix out of valueText. */}
                            <TotalItem
                                column="Originalquantity"
                                summaryType="sum"
                                valueFormat={quantityFormat}
                                displayFormat="{0}"
                                customizeText={(e) => format(MESSAGES.CH_SHIP_TOTAL, e.valueText)}
                            />
                        </Summary>
                    </DataGrid>
                </div>

                <OperationDialogFooter okText="Save" okDisabled={!shipment || busy} onOk={handleSave} onCancel={closePopup} />
            </div>

            <ShipItemPicker
                picker={picker}
                onPick={handlePick}
                onClose={() => setPicker(null)}
                onOpenStatusOnlyChange={handleOpenStatusOnlyChange}
            />
        </Popup>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(ChargeAndHoldShipmentPopup);
